import http from "@/services/http";

/**
 * Parametrização de agenda do médico: disponibilidades recorrentes (dia da
 * semana + faixa de horário + clínica, com vigência e regra de feriado) e
 * travas de período. Ver backend /api/doctor-schedule/*.
 */

// ─── Disponibilidades ────────────────────────────────────────────────

/** Lista disponibilidades do médico + notas de sombreamento persistentes. */
export async function getAvailabilities(doctorId) {
  const { data } = await http.get("/api/doctor-schedule/availabilities", {
    params: doctorId ? { doctor_id: doctorId } : {},
  });
  return {
    items: data?.data || [],
    overlapNotes: data?.overlap_notes || [],
  };
}

/**
 * Cria uma disponibilidade.
 * payload: { clinic_id, weekdays[], time_from, time_to, date_start, date_end?,
 *            applies_on_holiday, accept_overlap? }
 * Retorna { ok, overlap, conflicts, data }. overlap=true quando o backend
 * detecta sombreamento (HTTP 409) e accept_overlap não foi enviado.
 */
export async function createAvailability(payload) {
  try {
    const { data } = await http.post("/api/doctor-schedule/availabilities", payload);
    return { ok: true, overlap: false, data: data?.data };
  } catch (err) {
    const res = err.response;
    if (res?.status === 409 && res?.data?.status === "overlap") {
      return { ok: false, overlap: true, conflicts: res.data.conflicts || [] };
    }
    throw err;
  }
}

/** Atualiza uma disponibilidade (mesma semântica de sombreamento do create). */
export async function updateAvailability(id, payload) {
  try {
    const { data } = await http.put(`/api/doctor-schedule/availabilities/${id}`, payload);
    return { ok: true, overlap: false, data: data?.data };
  } catch (err) {
    const res = err.response;
    if (res?.status === 409 && res?.data?.status === "overlap") {
      return { ok: false, overlap: true, conflicts: res.data.conflicts || [] };
    }
    throw err;
  }
}

/** Remove uma disponibilidade. */
export function deleteAvailability(id) {
  return http.delete(`/api/doctor-schedule/availabilities/${id}`);
}

// ─── Travas ──────────────────────────────────────────────────────────

/** Lista travas do médico. */
export async function getBlocks(doctorId) {
  const { data } = await http.get("/api/doctor-schedule/blocks", {
    params: doctorId ? { doctor_id: doctorId } : {},
  });
  return data?.data || [];
}

/**
 * Cria uma trava.
 * payload: { clinic_id?, starts_at, ends_at, reason, reason_other? }
 * clinic_id ausente/null = todas as clínicas do médico.
 */
export async function createBlock(payload) {
  const { data } = await http.post("/api/doctor-schedule/blocks", payload);
  return data?.data;
}

/** Atualiza uma trava. */
export async function updateBlock(id, payload) {
  const { data } = await http.put(`/api/doctor-schedule/blocks/${id}`, payload);
  return data?.data;
}

/** Remove uma trava. */
export function deleteBlock(id) {
  return http.delete(`/api/doctor-schedule/blocks/${id}`);
}

// ─── Feriados ────────────────────────────────────────────────────────

/** Lista feriados (filtros opcionais: year, state, city, scope). */
export async function getHolidays(params = {}) {
  const { data } = await http.get("/api/holidays", { params });
  return data?.data || [];
}

/**
 * Resolve a agenda de uma clínica no intervalo (Fase 2): janelas disponíveis
 * (já aplicando vigência e regra de feriado) + travas, por médico.
 * Retorna { clinic_id, start_date, end_date, doctors: { [id]: { available, locked } } }.
 */
export async function getResolvedSchedule({ clinicId, doctorIds, startDate, endDate }) {
  const params = { clinic_id: clinicId, start_date: startDate, end_date: endDate };
  if (doctorIds && doctorIds.length) params.doctor_ids = doctorIds.join(",");
  const { data } = await http.get("/api/doctor-schedule/resolved", { params });
  return data?.data || null;
}

/**
 * Grade-base de horários selecionáveis no agendamento — FONTE ÚNICA.
 * É só a rede de segurança para encaixe de emergência: a disponibilidade REAL
 * (o que o médico parametrizou) vem do backend em /doctor-schedule/resolved e é
 * o que fica DESTACADO. O mixin de agenda une esta grade às janelas do dia, de
 * modo que os horários parametrizados sempre aparecem, mesmo fora dela.
 */
export const DAY_GRID = { startHour: 7, endHour: 18, stepMinutes: 30 };

/** Gera a lista "HH:MM" da grade-base (ou de um intervalo custom). */
export function buildTimeGrid({ startHour, endHour, stepMinutes } = DAY_GRID) {
  const slots = [];
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
}

/** Dias da semana (valor -> rótulo), seg..dom — mesma convenção do encaixe. */
export const WEEKDAYS = [
  { value: "mon", label: "Seg" },
  { value: "tue", label: "Ter" },
  { value: "wed", label: "Qua" },
  { value: "thu", label: "Qui" },
  { value: "fri", label: "Sex" },
  { value: "sat", label: "Sáb" },
  { value: "sun", label: "Dom" },
];

/** Motivos de trava (valor -> rótulo). */
export const BLOCK_REASONS = [
  { value: "congresso", label: "Congresso" },
  { value: "aula", label: "Aula" },
  { value: "curso", label: "Curso" },
  { value: "ferias", label: "Férias" },
  { value: "outro", label: "Outro" },
];
